// A token's hover card (issue 1127): the numbers its viewer may see — exactly what this person's projected state holds,
// so a player sees another's token only as that token shares it (design/16 §4, design/22), plus the facts the DM
// revealed (#1057) — and, on a token this person may act with, its actions, each fired at their target in one click.
// It follows the board's hover; it stays while the pointer moves onto it, so its buttons can be pressed.
import { useEffect, useRef, useState } from 'react';
import { conditionWithLevel, elevation, heightLabel, sceneFloors, floorOf, squares3D, heightSquares, type Fact, type Inferred, type Values } from '@dragons-table/vtt-shared';
import { selection } from './selection';
import { heightTipOn, onHeightTip } from './floorView';
import { boardBus, type TokenHover } from './bus';
import { useTarget } from './target';
import { useFire, useTokenActions } from '../shell/panels/ActionParts';
import { useInferredLines } from '../shell/panels/EntryParts';
import { useTableState } from '../table/TableState';
import { useMayAct } from '../table/mayAct';
import { hpWords, useNumberWords, type NumberWords } from '../table/numberWords';

interface Seen { id: string; kind: string; name?: string; mine?: boolean; values?: Values; facts?: Fact[]; inferred?: Inferred; character?: string | null; entry?: unknown; dead?: boolean }

const btn = 'rounded border border-amber-700 px-1.5 py-0.5 text-[0.6875rem] hover:bg-stone-800 disabled:opacity-50';

const signed = (n: number): string => `${n >= 0 ? '+' : ''}${n}`;

/**
 * What the linked sheet added to one of the token's own numbers (issue 1505; docs/MRCS/feats.md §7), each by its label —
 * " (Toughness +3)" — when the sheet itemised that number and the token still holds the sheet's value; else nothing,
 * since a number set by hand at the table is no longer the sheet's working. The sheet's numbers reach only the DM and
 * the token's owners, so no one else is shown them.
 */
function sheetWorking(v: Values, purpose: string, shown: number | undefined): string {
  const n = v.sheet?.find((x) => x.purpose === purpose && x.lines && x.lines.length > 1);
  if (!n || shown !== n.value) return '';
  return ` (${n.lines!.slice(1).map((l) => `${l.label} ${signed(l.value)}`).join(', ')})`;
}

/** The numbers a token carries for this viewer, as short lines in the system's words (issue 1309); none when it carries none. */
export function seenNumbers(v: Values | undefined, w: NumberWords): string[] {
  if (!v) return [];
  const out: string[] = [];
  if (v.hp) out.push(`${hpWords(v.hp, w)}${sheetWorking(v, 'hp_max', v.hp.max)}`);
  if (typeof v.ac === 'number') out.push(`${w.defence} ${v.ac}${sheetWorking(v, 'armor_class', v.ac)}`);
  for (const s of [v.slot1, v.slot2]) if (s) out.push(`${s.label} ${s.value}${typeof s.max === 'number' ? `/${s.max}` : ''}`);
  if (typeof v.initBonus === 'number') out.push(`${w.initiative} ${signed(v.initBonus)}${sheetWorking(v, 'initiative', v.initBonus)}`);
  if (v.conditions?.length) out.push(v.conditions.map((c) => conditionWithLevel(c, v.condition_levels)).join(', '));
  return out;
}

type Placed = { x: number; y: number; floor?: string; z?: number; mine?: boolean; owners?: string[] };
/**
 * The height tooltip (qa-runs#1160; design/32 §5): the token's height and floor ("40 ft · Sky"), and with one of this
 * person's own tokens selected (the DM's: any), the distance to it counting height ("45 ft away") — from what this screen holds.
 */
export function heightLine(state: { objects: Record<string, unknown>; scene: { floors?: import('@dragons-table/vtt-shared').Floor[] }; grid: { x: { basePitch: number }; y: { basePitch: number } } }, o: Placed, fromId: string | undefined, meId: string, distance: { unit: string; perSquare: number; diagonal: import('@dragons-table/vtt-shared').Diagonal } | null, dm = false): string | null {
  const floors = state.scene.floors && state.scene.floors.length > 1 ? state.scene.floors : null;
  const z = elevation(o, state.scene);
  const parts: string[] = [];
  if (floors || z) parts.push(heightLabel(z, floors ? (sceneFloors(state.scene).find((f) => f.id === floorOf(o, sceneFloors(state.scene)))?.name ?? null) : null, distance));
  const from = fromId ? (state.objects[fromId] as Placed | undefined) : undefined;
  if (from && distance && (dm || from.mine || from.owners?.includes(meId))) {
    const dx = Math.round((o.x - from.x) / state.grid.x.basePitch); const dy = Math.round((o.y - from.y) / state.grid.y.basePitch);
    const sq = squares3D(dx, dy, heightSquares(z - elevation(from, state.scene), distance.diagonal), distance.diagonal);
    parts.push(`${Math.round(sq * distance.perSquare * 10) / 10} ${distance.unit} away`);
  }
  return parts.length ? parts.join(' · ') : null;
}

export function TokenHoverCard() {
  const { store, systemRules } = useTableState();
  const mayAct = useMayAct();
  const words = useNumberWords();
  const [h, setH] = useState<TokenHover | null>(null);
  const over = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => boardBus.onHoverToken((next) => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (next) { setH(next); return; }
    // Leaving the token: a moment to reach the card before it goes.
    timer.current = setTimeout(() => { if (!over.current) setH(null); }, 300);
  }), []);
  const o = h ? (store.state.objects[h.id] as Seen | undefined) : undefined;
  const isDm = mayAct('attack.request');
  const may = Boolean(o && o.kind === 'token' && (isDm || o.mine));
  // Its actions are attacks: none where the table can resolve none (issue 1305).
  const actions = useTokenActions(o, may && systemRules.attack !== null);
  const target = useTarget();
  const fire = useFire();
  // What the players worked out of it (issue 1133): only what this viewer was sent.
  const worked = useInferredLines(o);
  const [tip, setTip] = useState(heightTipOn());
  useEffect(() => onHeightTip(setTip), []);
  const { meId } = useTableState();
  if (!h || !o || o.kind !== 'token') return null;
  const numbers = seenNumbers(o.values, words);
  const aimed = target && target !== o.id ? (store.state.objects[target] as Seen | undefined) : undefined;
  const list = actions && actions !== 'asking' ? actions.actions : [];
  const height = tip ? heightLine(store.state, o as unknown as Placed, h.from ?? [...selection.get()].find((id) => id !== o.id), meId, systemRules.distance, isDm) : null;
  return (
    <div role="tooltip" data-testid="token-hover-card" className="fixed z-40 max-w-[16rem] vtt-popover px-2 py-1 text-xs shadow-lg"
      style={{ left: Math.min(h.at.x + 14, window.innerWidth - 270), top: Math.min(h.at.y + 14, window.innerHeight - 160) }}
      onPointerEnter={() => { over.current = true; }} onPointerLeave={() => { over.current = false; setH(null); }}>
      <p className="vtt-title text-[0.8125rem]">{o.name ?? <span className="italic text-stone-400">unnamed</span>}{o.dead ? ' (dead)' : ''}</p>
      {height && <p className="text-stone-200" data-testid="hover-height">{height}</p>}
      {numbers.length > 0 && <p className="text-stone-300" data-testid="hover-numbers">{numbers.join(' · ')}</p>}
      {(o.facts ?? []).map((f) => <p key={f.label} className="text-stone-400"><span className="text-stone-500">{f.label}:</span> {f.value}</p>)}
      {worked.map((l) => <p key={l} className="text-stone-400" data-testid="hover-inferred">{l}</p>)}
      {may && list.length > 0 && (
        <div className="mt-1 flex flex-wrap items-center gap-1" data-testid="hover-actions">
          {list.slice(0, 6).map((a) => (
            <button key={a.key} type="button" className={btn} disabled={!aimed} title={aimed ? `${a.spec.name} at ${aimed.name ?? 'the target'}` : 'Target a token first (its menu: Target).'}
              onClick={() => { if (aimed) fire(o.id, aimed.id, a.spec, true); }}>{a.label}</button>
          ))}
          <span className="text-stone-500">{aimed ? `at ${aimed.name ?? 'the target'}` : 'no target'}</span>
        </div>
      )}
    </div>
  );
}
